import { Delegate } from './delegate.js';

const MOBILE_LAYOUT_QUERY = '(max-width: 767px)';
const SAVE_WITH_KITS_MOBILE_BODY_NAV_PADDING = 16;
const SAVE_WITH_KITS_DESKTOP_BELOW_CHROME_PX = 10;
const SAVE_WITH_KITS_REVEAL_DELAY_MS = 100;
const OVERLAY_BODY_CLASSES = [
  'search-open',
  'has-dropdown-menu',
  'modal-show',
  'modal-showing',
  'hr-search-disable-scroll',
];
const HELLO_RETAIL_RESULTS_SELECTOR = '.hr-results';

class ProductSaveWithKits extends HTMLElement {
  #abortController = null;
  #delegate = null;
  #headerObserver = null;
  #kitsPanelObserver = null;
  #overlayObserver = null;
  #overlaySyncRaf = null;
  #layoutScrollRaf = null;
  #mobileLayoutMq = null;
  #panel = null;
  #panelOriginalNextSibling = null;
  #panelOriginalParent = null;
  #desktopTopFrozen = false;
  #prefersReducedMotion = false;
  #revealTimeoutId = null;
  #topSyncPending = false;
  #topSyncPostRaf = null;
  #topSyncRaf = null;
  #userDismissed = false;

  connectedCallback() {
    if (this.#abortController) return;

    this.#panel = this.querySelector('[data-save-with-kits-panel]');
    if (!this.#panel) return;
    this.#mountPanelToBody();

    this.#abortController = new AbortController();
    this.#mobileLayoutMq = window.matchMedia?.(MOBILE_LAYOUT_QUERY); // matchMedia: legacy theme has no responsive utility module.
    this.#prefersReducedMotion = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

    this.#bindDelegatedEvents();
    this.#bindGlobalEvents();
    this.#observeKitsPanel();
    this.#observeHeaderChrome();
    this.#observeOverlayState();

    this.#syncSaveWithKitsTop();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
    this.#scheduleSyncSaveWithKitsTop();
    this.#scheduleReveal();
  }

  disconnectedCallback() {
    if (!this.#abortController) return;
    this.#destroy();
  }

  #bindDelegatedEvents() {
    this.#delegate = new Delegate(this.#panel);
    this.#delegate.on('click', '[data-action="close-kits-popup"]', this.#onCloseClick);
    this.#delegate.on('click', '[data-action="toggle-kits-panel"]', this.#onMobileTriggerClick);
    this.#delegate.on('keypress', '[data-action="close-kits-popup"], [data-action="toggle-kits-panel"]', this.#onActionKeypress);
  }

  #mountPanelToBody() {
    if (!this.#panel) return;
    if (this.#panel.parentNode === document.body) return;

    this.#panelOriginalParent = this.#panel.parentNode;
    this.#panelOriginalNextSibling = this.#panel.nextSibling;
    document.body.appendChild(this.#panel);
  }

  #restorePanelMount() {
    if (!this.#panelOriginalParent || !this.#panel) return;

    if (this.#panel.parentNode === document.body) {
      if (
        this.#panelOriginalNextSibling &&
        this.#panelOriginalNextSibling.parentNode === this.#panelOriginalParent
      ) {
        this.#panelOriginalParent.insertBefore(this.#panel, this.#panelOriginalNextSibling);
      } else {
        this.#panelOriginalParent.appendChild(this.#panel);
      }
    }

    this.#panelOriginalParent = null;
    this.#panelOriginalNextSibling = null;
  }

  #bindGlobalEvents() {
    const { signal } = this.#abortController;

    document.addEventListener('keydown', this.#onKeydown, { signal }); // delegate-exempt: global Escape key handling.
    window.addEventListener('resize', this.#onResize, { passive: true, signal }); // delegate-exempt: viewport resize has no DOM target.
    window.addEventListener('load', this.#onLoad, { once: true, signal }); // delegate-exempt: one-time page load sync.
    window.addEventListener('scroll', this.#onScrollLayoutSync, { passive: true, signal }); // delegate-exempt: viewport scroll drives popup position.
    window.addEventListener('wheel', this.#resyncAfterHeader, { passive: true, once: true, signal }); // delegate-exempt: one-time header height sync after interaction.
    window.addEventListener('touchstart', this.#resyncAfterHeader, { passive: true, once: true, signal }); // delegate-exempt: one-time header height sync after interaction.
    document.addEventListener('drawer:handleAfterShow', this.#syncBehindOverlay, { signal }); // delegate-exempt: hide kits while drawers are open.
    document.addEventListener('drawer:handleAfterHide', this.#syncBehindOverlay, { signal }); // delegate-exempt: restore kits after drawers close.
    document.addEventListener('sticky-atc:change', this.#onStickyAtcChange, { signal }); // delegate-exempt: sync chat offset when sticky ATC toggles.
    this.#panel?.addEventListener('transitionrun', this.#onPanelTransition, { passive: true, signal }); // delegate-exempt: keep CSS vars synced while mobile panel animates.
    this.#panel?.addEventListener('transitionend', this.#onPanelTransition, { passive: true, signal }); // delegate-exempt: final sync after open/close transition.

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.#onVisualViewportResize, { passive: true, signal }); // delegate-exempt: visual viewport resize has no DOM target.
    }

    if (this.#mobileLayoutMq?.addEventListener) {
      this.#mobileLayoutMq.addEventListener('change', this.#onMobileLayoutMqChange);
    } else if (this.#mobileLayoutMq?.addListener) {
      this.#mobileLayoutMq.addListener(this.#onMobileLayoutMqChange);
    }
  }

  #observeKitsPanel() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#kitsPanelObserver = new ResizeObserver(() => {
      this.#syncMobileBarInset();
      this.#syncSaveWithKitsMobileBodyMaxHeight();
    });
    this.#kitsPanelObserver.observe(this.#panel);
  }

  #observeHeaderChrome() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#headerObserver = new ResizeObserver(() => this.#scheduleSyncSaveWithKitsTop());
    [
      document.querySelector('header[is="sticky-header"]'),
      document.querySelector('header[is="basic-header"]'),
      document.querySelector('.shopify-section-group-header-group'),
      document.querySelector('announcement-bar') || document.querySelector('.announcement-bar'),
      document.querySelector('.topbar-section'),
      document.getElementById('theme-header'),
    ]
      .filter(Boolean)
      .forEach((el) => this.#headerObserver.observe(el));
  }

  #onCloseClick = (event) => {
    event.preventDefault();
    this.#dismiss();
  };

  #onMobileTriggerClick = (event, trigger) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.#isMobileSaveWithKitsLayout()) return;
    if (!this.#panel.classList.contains('is-revealed')) return;
    if (trigger.hasAttribute('hidden')) return;

    this.#setMobileExpanded(!this.#panel.classList.contains('save-with-kits-mobile-expanded'));
  };

  #onActionKeypress = (event, target) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    if (target.dataset.action === 'close-kits-popup') {
      this.#onCloseClick(event);
      return;
    }

    if (target.dataset.action === 'toggle-kits-panel') {
      this.#onMobileTriggerClick(event, target);
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#panel.classList.contains('is-revealed')) return;

    if (this.#isMobileSaveWithKitsLayout() && this.#panel.classList.contains('save-with-kits-mobile-expanded')) {
      this.#setMobileExpanded(false);
      return;
    }

    this.#dismiss();
  };

  #onResize = () => {
    this.#clearFrozenDesktopTop();
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  };

  #onVisualViewportResize = () => {
    this.#clearFrozenDesktopTop();
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  };

  #onMobileLayoutMqChange = () => {
    this.#clearFrozenDesktopTop();
    this.#panel.classList.remove('save-with-kits-mobile-expanded');
    this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', 'false');
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  };

  #onLoad = () => {
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
    requestAnimationFrame(() => this.#syncMobileBarInset());
    setTimeout(() => this.#syncMobileBarInset(), 160);
    setTimeout(() => this.#syncMobileBarInset(), 420);
  };

  #onPanelTransition = () => {
    if (!this.#isMobileSaveWithKitsLayout()) return;
    this.#syncMobileBarInset();
    requestAnimationFrame(() => this.#syncMobileBarInset());
  };

  #resyncAfterHeader = () => {
    this.#scheduleSyncSaveWithKitsTop();
  };

  #onStickyAtcChange = () => {
    this.#syncMobileBarInset();
    requestAnimationFrame(() => this.#syncMobileBarInset());
  };

  #isHelloRetailSearchOpen() {
    if (document.documentElement.classList.contains('hr-search-disable-scroll')) return true;

    const hrResults = document.querySelector(HELLO_RETAIL_RESULTS_SELECTOR);
    if (!hrResults) return false;

    const style = getComputedStyle(hrResults);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
      return false;
    }

    const rect = hrResults.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  #isBehindOverlay() {
    if (OVERLAY_BODY_CLASSES.some((className) => document.body.classList.contains(className))) {
      return true;
    }

    if (this.#isHelloRetailSearchOpen()) return true;

    return Boolean(document.querySelector('.drawer[open]'));
  }

  #syncBehindOverlay = () => {
    if (!this.#panel || this.#userDismissed) return;

    const behind = this.#isBehindOverlay();
    const wasBehind = this.#panel.classList.contains('save-with-kits--behind-overlay');
    this.#panel.classList.toggle('save-with-kits--behind-overlay', behind);

    if (!behind) {
      if (wasBehind && this.#panel.classList.contains('is-revealed')) {
        this.#syncMobileBarInset();
        requestAnimationFrame(() => this.#syncMobileBarInset());
      }
      return;
    }

    this.#panel.classList.remove('save-with-kits-mobile-expanded');
    this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', 'false');

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#clearMobileBarInset();
      this.#syncSaveWithKitsMobileBodyMaxHeight();
    }
  };

  #scheduleBehindOverlaySync() {
    if (this.#overlaySyncRaf != null) return;

    this.#overlaySyncRaf = requestAnimationFrame(() => {
      this.#overlaySyncRaf = null;
      this.#syncBehindOverlay();
    });
  }

  #observeOverlayState() {
    if (typeof MutationObserver === 'undefined') {
      this.#syncBehindOverlay();
      return;
    }

    this.#overlayObserver = new MutationObserver(() => this.#scheduleBehindOverlaySync());
    this.#overlayObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
    this.#overlayObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open'],
    });
    this.#syncBehindOverlay();
  }

  #isMobileSaveWithKitsLayout() {
    return Boolean(this.#mobileLayoutMq?.matches);
  }

  #clearMobileBarInset() {
    document.body.classList.remove('save-with-kits-mobile-bar-enabled');
    document.documentElement.style.removeProperty('--save-with-kits-mobile-bar-height');
    document.documentElement.style.removeProperty('--save-with-kits-popup-height');
    document.documentElement.style.removeProperty('--save-with-kits-trigger-height');
    document.documentElement.style.removeProperty('--save-with-kits-sticky-offset');
    document.querySelectorAll('.bb-feedback-button, .bb-feedback-button.gleap-font.gl-block').forEach((el) => {
      el.style.removeProperty('bottom');
    });
  }

  #syncFeedbackButtonBottom(bottomPx) {
    const bottom = Math.max(0, Math.round(bottomPx || 0));
    document.querySelectorAll('.bb-feedback-button, .bb-feedback-button.gleap-font.gl-block').forEach((el) => {
      el.style.setProperty('bottom', `${bottom}px`, 'important');
      el.style.setProperty('transition', 'bottom 0.55s cubic-bezier(0.22, 1, 0.36, 1)', 'important');
    });
  }

  #syncMobileBarInset() {
    const docEl = document.documentElement;
    if (!this.#isMobileSaveWithKitsLayout()) {
      this.#clearMobileBarInset();
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      return;
    }

    if (!this.#panel.classList.contains('is-revealed')) {
      this.#clearMobileBarInset();
      const stickyAtc = this.#getStickyAtcOffsetPx();
      const mobileSticky = this.#parseCssPx(getComputedStyle(document.documentElement).getPropertyValue('--mobile-sticky-bar-height'));
      const stickyOnlyOffset = stickyAtc > 0 ? stickyAtc + 16 : mobileSticky + 100 + 16;
      this.#syncFeedbackButtonBottom(stickyOnlyOffset);
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      return;
    }

    document.body.classList.add('save-with-kits-mobile-bar-enabled');
    const height = Math.ceil(this.#panel.getBoundingClientRect().height);
    const triggerBar = this.#panel.querySelector('.product__save-with-kits-bar');
    const triggerHeight = triggerBar ? Math.ceil(triggerBar.getBoundingClientRect().height) : 0;
    const computedStyle = getComputedStyle(docEl);
    const stickyOffset = this.#getStickyAtcOffsetPx();

    docEl.style.setProperty('--save-with-kits-mobile-bar-height', `${height}px`);
    docEl.style.setProperty('--save-with-kits-popup-height', `${height}px`);
    docEl.style.setProperty('--save-with-kits-trigger-height', `${triggerHeight}px`);
    docEl.style.setProperty('--save-with-kits-sticky-offset', `${stickyOffset}px`);
    const feedbackBottom = this.#panel.classList.contains('save-with-kits-mobile-expanded')
      ? stickyOffset + height + 16
      : stickyOffset + triggerHeight + 16;
    this.#syncFeedbackButtonBottom(feedbackBottom);
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  }

  #setMobileExpanded(expanded) {
    if (!this.#isMobileSaveWithKitsLayout()) return;

    this.#panel.classList.toggle('save-with-kits-mobile-expanded', expanded);
    this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.#syncMobileBarInset();

    if (!expanded) {
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      return;
    }

    requestAnimationFrame(() => {
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      this.#updateKitsCarousel();
    });
  }

  #updateKitsCarousel() {
    const slider = this.#panel.querySelector('save-with-kits-cards')?.carousel?.slider;
    if (!slider || typeof slider.update !== 'function') return;

    requestAnimationFrame(() => {
      slider.update();
      if (typeof slider.updateSize === 'function') slider.updateSize();
    });
  }

  #dismiss() {
    this.#userDismissed = true;
    this.#panel.classList.remove('is-revealed', 'save-with-kits-mobile-expanded');
    this.#panel.setAttribute('aria-hidden', 'true');
    this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', 'false');
    this.#clearMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  }

  #parseCssPx(value) {
    const parsed = parseFloat(String(value || '').trim());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  #getStickyAtcOffsetPx() {
    const stickySelectors = [
      '.sticky-atc-bar.sticky-atc-bar--show',
      'sticky-atc-bar.sticky-atc-bar--show',
      'sticky-atc-bar-minimalist.sticky-atc-bar--show',
    ];
    const visibleBars = [];

    stickySelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => visibleBars.push(el));
    });

    if (!visibleBars.length) return 0;

    const docStyle = getComputedStyle(document.documentElement);
    const isMinimalist = visibleBars.some((el) => el.tagName.toLowerCase() === 'sticky-atc-bar-minimalist');
    const isClassic = visibleBars.some(
      (el) => el.classList.contains('sticky-atc-bar') && el.tagName.toLowerCase() !== 'sticky-atc-bar-minimalist'
    );
    let stickyHeight = this.#parseCssPx(docStyle.getPropertyValue('--sticky-atc-bar-height'));

    if (!stickyHeight) {
      visibleBars.forEach((el) => {
        stickyHeight = Math.max(stickyHeight, el.offsetHeight || el.clientHeight || 0);
      });
    }

    if (isMinimalist && !isClassic) return stickyHeight;

    const mobileSticky = document.body.classList.contains('mobile-sticky-bar-down')
      ? 0
      : this.#parseCssPx(docStyle.getPropertyValue('--mobile-sticky-bar-height'));

    return mobileSticky + stickyHeight;
  }

  #syncSaveWithKitsMobileBodyMaxHeight() {
    const docEl = document.documentElement;

    if (!this.#isMobileSaveWithKitsLayout()) {
      docEl.style.removeProperty('--save-with-kits-mobile-body-max-height');
      return;
    }

    const bar = this.#panel.querySelector('.product__save-with-kits-bar');
    const barHeight = bar ? Math.ceil(bar.getBoundingClientRect().height) : 0;
    const computedStyle = getComputedStyle(docEl);
    const mobileSticky = this.#parseCssPx(computedStyle.getPropertyValue('--mobile-sticky-bar-height'));
    const stickyAtc = this.#getStickyAtcOffsetPx();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const available = viewportHeight - mobileSticky - stickyAtc - barHeight - SAVE_WITH_KITS_MOBILE_BODY_NAV_PADDING;

    docEl.style.setProperty('--save-with-kits-mobile-body-max-height', `${Math.max(160, Math.floor(available))}px`);
  }

  #getViewportHeaderTopPx() {
    const top = this.#minTopOf([
      document.querySelector('.shopify-section-group-header-group'),
      document.querySelector('header[is="sticky-header"]'),
    ]);

    return Math.max(0, Number.isFinite(top) ? top : 0);
  }

  #minTopOf(elements) {
    return elements.filter(Boolean).reduce((min, element) => Math.min(min, element.getBoundingClientRect().top), Infinity);
  }

  #maxBottomOf(elements) {
    return elements.filter(Boolean).reduce((max, element) => {
      const rect = element.getBoundingClientRect();
      if (rect.height < 1) return max;
      return Math.max(max, Math.ceil(rect.bottom));
    }, 0);
  }

  #getHeaderChromeBottomPx() {
    const group = document.querySelector('.shopify-section-group-header-group');
    const groupSections = group ? Array.from(group.querySelectorAll(':scope > .shopify-section')) : [];
    const headerChrome = Array.from(
      document.querySelectorAll(
        '.announcement-bar-section, .topbar-section, #theme-header, header[is="sticky-header"], header[is="basic-header"], .header__top, .header__bottom'
      )
    );

    return this.#maxBottomOf([group, ...groupSections, ...headerChrome]);
  }

  #clearFrozenDesktopTop() {
    this.#desktopTopFrozen = false;
    this.#panel?.style.removeProperty('top');
  }

  #freezeDesktopTop() {
    if (this.#isMobileSaveWithKitsLayout() || !this.#panel) return;

    const chromeBottom = this.#getHeaderChromeBottomPx();
    const top = chromeBottom + SAVE_WITH_KITS_DESKTOP_BELOW_CHROME_PX;

    this.#panel.style.top = `calc(${Math.round(top)}px + 0.5rem)`;
    this.#desktopTopFrozen = true;
  }

  #waitForStableHeaderChrome() {
    const fontsReady = document.fonts?.ready ?? Promise.resolve();

    return fontsReady.then(
      () =>
        new Promise((resolve) => {
          let lastBottom = -1;
          let stableFrames = 0;
          let frames = 0;

          const tick = () => {
            if (!this.#abortController) {
              resolve();
              return;
            }

            const bottom = this.#getHeaderChromeBottomPx();

            if (bottom > 0 && bottom === lastBottom) {
              stableFrames += 1;
              if (stableFrames >= 3) {
                resolve();
                return;
              }
            } else {
              stableFrames = 0;
              lastBottom = bottom;
            }

            frames += 1;
            if (frames >= 60) {
              resolve();
              return;
            }

            requestAnimationFrame(tick);
          };

          requestAnimationFrame(tick);
        })
    );
  }

  #syncSaveWithKitsTop() {
    if (this.#desktopTopFrozen) return;

    const docEl = document.documentElement;

    if (this.#isMobileSaveWithKitsLayout()) {
      docEl.style.removeProperty('--save-with-kits-desktop-top');
      docEl.style.removeProperty('--save-with-kits-header-top');
      docEl.style.removeProperty('--save-with-kits-header-bottom');
      docEl.style.removeProperty('--save-with-kits-popup-margin-top');
      return;
    }

    const headerTop = this.#getViewportHeaderTopPx();
    const chromeBottom = this.#getHeaderChromeBottomPx();
    const top = chromeBottom + SAVE_WITH_KITS_DESKTOP_BELOW_CHROME_PX;

    docEl.style.setProperty('--save-with-kits-desktop-top', `${Math.round(top)}px`);
    docEl.style.setProperty('--save-with-kits-header-top', `${headerTop}px`);
    docEl.style.setProperty('--save-with-kits-header-bottom', `${chromeBottom}px`);
    docEl.style.setProperty('--save-with-kits-popup-margin-top', `${SAVE_WITH_KITS_DESKTOP_BELOW_CHROME_PX}px`);
  }

  #scheduleSyncSaveWithKitsTop() {
    if (this.#topSyncPending) return;

    this.#topSyncPending = true;
    this.#topSyncRaf = requestAnimationFrame(() => {
      this.#topSyncRaf = null;
      this.#topSyncPostRaf = requestAnimationFrame(() => {
        this.#topSyncPostRaf = null;
        this.#topSyncPending = false;
        this.#syncSaveWithKitsTop();
      });
    });
  }

  #onScrollLayoutSync = () => {
    if (this.#layoutScrollRaf != null) return;

    this.#layoutScrollRaf = requestAnimationFrame(() => {
      this.#layoutScrollRaf = null;
      this.#scheduleSyncSaveWithKitsTop();
      this.#syncMobileBarInset();
    });
  };

  #prepareRevealPosition() {
    this.#syncSaveWithKitsTop();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  }

  #finishReveal() {
    if (!this.#abortController || this.#userDismissed) return;

    if (this.#prefersReducedMotion) {
      this.#panel.classList.add('save-with-kits--no-motion');
    }

    this.#reveal();
  }

  #reveal() {
    if (this.#userDismissed) return;
    if (this.#panel.classList.contains('is-revealed')) return;

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#prepareRevealPosition();
    } else if (!this.#desktopTopFrozen) {
      this.#freezeDesktopTop();
    }

    this.#panel.classList.add('is-revealed');
    this.#panel.setAttribute('aria-hidden', 'false');

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#panel.classList.remove('save-with-kits-mobile-expanded');
      this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', 'false');
    }

    this.#syncMobileBarInset();
    this.#syncBehindOverlay();
    requestAnimationFrame(() => {
      this.#syncMobileBarInset();
      this.#syncBehindOverlay();
    });
  }

  #scheduleReveal() {
    this.#revealTimeoutId = window.setTimeout(() => {
      this.#revealTimeoutId = null;
      this.#runRevealSequence();
    }, SAVE_WITH_KITS_REVEAL_DELAY_MS);
  }

  #runRevealSequence() {
    if (!this.#abortController || this.#userDismissed) return;

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#prepareRevealPosition();

      requestAnimationFrame(() => {
        if (!this.#abortController || this.#userDismissed) return;

        this.#prepareRevealPosition();
        requestAnimationFrame(() => this.#finishReveal());
      });

      return;
    }

    this.#waitForStableHeaderChrome().then(() => {
      if (!this.#abortController || this.#userDismissed) return;

      this.#freezeDesktopTop();

      requestAnimationFrame(() => {
        if (!this.#abortController || this.#userDismissed) return;

        this.#freezeDesktopTop();
        requestAnimationFrame(() => this.#finishReveal());
      });
    });
  }

  #cancelRevealTimeout() {
    if (this.#revealTimeoutId == null) return;

    clearTimeout(this.#revealTimeoutId);
    this.#revealTimeoutId = null;
  }

  #cancelRafs() {
    [this.#layoutScrollRaf, this.#overlaySyncRaf, this.#topSyncRaf, this.#topSyncPostRaf]
      .filter((rafId) => rafId != null)
      .forEach((rafId) => cancelAnimationFrame(rafId));

    this.#layoutScrollRaf = null;
    this.#overlaySyncRaf = null;
    this.#topSyncRaf = null;
    this.#topSyncPostRaf = null;
    this.#topSyncPending = false;
  }

  #clearDocumentStyles() {
    const docEl = document.documentElement;

    this.#panel?.classList.remove('save-with-kits--behind-overlay');
    this.#clearFrozenDesktopTop();
    this.#clearMobileBarInset();
    docEl.style.removeProperty('--save-with-kits-mobile-body-max-height');
    docEl.style.removeProperty('--save-with-kits-desktop-top');
    docEl.style.removeProperty('--save-with-kits-header-top');
    docEl.style.removeProperty('--save-with-kits-header-bottom');
    docEl.style.removeProperty('--save-with-kits-popup-margin-top');
  }

  #destroy() {
    this.#abortController?.abort();
    this.#delegate?.destroy();

    if (this.#mobileLayoutMq?.removeEventListener) {
      this.#mobileLayoutMq.removeEventListener('change', this.#onMobileLayoutMqChange);
    } else if (this.#mobileLayoutMq?.removeListener) {
      this.#mobileLayoutMq.removeListener(this.#onMobileLayoutMqChange);
    }

    this.#kitsPanelObserver?.disconnect();
    this.#headerObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#cancelRafs();
    this.#cancelRevealTimeout();
    this.#clearDocumentStyles();
    this.#restorePanelMount();

    this.#abortController = null;
    this.#delegate = null;
    this.#headerObserver = null;
    this.#kitsPanelObserver = null;
    this.#overlayObserver = null;
    this.#mobileLayoutMq = null;
    this.#panel = null;
    this.#panelOriginalNextSibling = null;
    this.#panelOriginalParent = null;
  }
}

if (!customElements.get('product-save-with-kits')) {
  customElements.define('product-save-with-kits', ProductSaveWithKits);
}
